import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3013/api";

const HukamnamaDisplay = () => {
  const [hukamnama, setHukamnama] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [fontSizes, setFontSizes] = useState({ gurmukhi: 28, punjabi: 24, english: 18 });
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pongTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Calculate optimal font sizes - adaptive to fill available space
  const calculateFontSizes = useCallback((linesData) => {
    if (!linesData || linesData.length === 0) return;

    const totalLines = linesData.length;
    const totalGurmukhiChars = linesData.reduce((sum, l) => sum + (l.gurmukhi?.length || 0), 0);
    const totalPunjabiChars = linesData.reduce((sum, l) => sum + (l.translationPunjabi?.length || 0), 0);
    const totalEnglishChars = linesData.reduce((sum, l) => sum + (l.translationEnglish?.length || 0), 0);
    const maxChars = Math.max(totalGurmukhiChars, totalPunjabiChars, totalEnglishChars);
    
    // Calculate content density score (lower = less content = bigger fonts)
    const densityScore = (totalLines * 15) + (maxChars / 30);
    
    let gurmukhiSize, punjabiSize, englishSize;

    // Calibrated adaptive sizing
    if (densityScore < 60) {
      // Very short - fill the screen
      gurmukhiSize = 44;
      punjabiSize = 36;
      englishSize = 27;
    } else if (densityScore < 100) {
      gurmukhiSize = 38;
      punjabiSize = 32;
      englishSize = 25;
    } else if (densityScore < 150) {
      gurmukhiSize = 34;
      punjabiSize = 28;
      englishSize = 23;
    } else if (densityScore < 200) {
      gurmukhiSize = 30;
      punjabiSize = 25;
      englishSize = 21;
    } else if (densityScore < 260) {
      gurmukhiSize = 26;
      punjabiSize = 22;
      englishSize = 19;
    } else if (densityScore < 320) {
      gurmukhiSize = 23;
      punjabiSize = 19;
      englishSize = 18;
    } else if (densityScore < 400) {
      gurmukhiSize = 21;
      punjabiSize = 17;
      englishSize = 16;
    } else if (densityScore < 500) {
      gurmukhiSize = 19;
      punjabiSize = 16;
      englishSize = 15;
    } else if (densityScore < 600) {
      gurmukhiSize = 17;
      punjabiSize = 14;
      englishSize = 14;
    } else {
      // Very long hukamnama
      gurmukhiSize = 15;
      punjabiSize = 13;
      englishSize = 13;
    }

    setFontSizes({ gurmukhi: gurmukhiSize, punjabi: punjabiSize, english: englishSize });
  }, []);

  // Fetch today's Hukamnama
  const fetchHukamnama = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/hukamnama/today`);
      if (response.data?.success && response.data.data) {
        setHukamnama(response.data.data);
        
        const shabadId = response.data.data.shabadId;
        const shabadResponse = await axios.get(`${API_URL}/shabads/${shabadId}`);
        if (shabadResponse.data?.success) {
          const linesData = shabadResponse.data.data.lines || [];
          setLines(linesData);
          calculateFontSizes(linesData);
        }
      }
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch Hukamnama:", err);
      setError("Failed to load Hukamnama");
      setLoading(false);
    }
  }, [calculateFontSizes]);

  // Start ping interval to keep connection alive
  const startPingInterval = useCallback(() => {
    if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
    if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);

    pingIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
          pongTimeoutRef.current = setTimeout(() => {
            if (wsRef.current) wsRef.current.close();
          }, 5000);
        } catch (err) {
          console.error("Failed to send ping:", err);
        }
      }
    }, 25000);
  }, []);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const wsUrl = API_URL.replace(/^http/, "ws").replace("/api", "") + "/ws/";
    
    try {
      wsRef.current = new WebSocket(wsUrl, ["display"]);

      wsRef.current.onopen = () => {
        console.log("WebSocket connected");
        setConnected(true);
        setError(null);
        startPingInterval();
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === "pong") {
            if (pongTimeoutRef.current) {
              clearTimeout(pongTimeoutRef.current);
              pongTimeoutRef.current = null;
            }
            return;
          }
          
          // Handle hukamnama updates - refresh display automatically
          if (message.type === "hukamnama") {
            console.log("[Hukamnama] Received update:", message.action);
            fetchHukamnama();
          }
          
          // Also handle display events that might affect hukamnama
          if (message.type === "display" && message.action === "hukamnama") {
            console.log("[Hukamnama] Display hukamnama event received");
            fetchHukamnama();
          }
        } catch (err) {
          console.error("Failed to parse WebSocket message:", err);
        }
      };

      wsRef.current.onclose = () => {
        console.log("WebSocket disconnected");
        setConnected(false);
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 2000);
      };

      wsRef.current.onerror = (err) => {
        console.error("WebSocket error:", err);
      };
    } catch (err) {
      console.error("Failed to create WebSocket:", err);
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, 2000);
    }
  }, [startPingInterval, fetchHukamnama]);

  // Initial setup
  useEffect(() => {
    fetchHukamnama();
    connectWebSocket();

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
    };
  }, [fetchHukamnama, connectWebSocket]);

  // Loading state
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.khanda}>☬</div>
        <div style={styles.loadingText}>Loading Hukamnama Sahib...</div>
        <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Gurmukhi:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </div>
    );
  }

  // Error or no Hukamnama
  if (error || !hukamnama || lines.length === 0) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.khanda}>☬</div>
        <div style={styles.errorGurmukhi}>ਅੱਜ ਦਾ ਹੁਕਮਨਾਮਾ ਉਪਲਬਧ ਨਹੀਂ ਹੈ</div>
        <div style={styles.errorEnglish}>Today's Hukamnama is not available</div>
        <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Gurmukhi:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </div>
    );
  }

  // Always show today's date (not the saved hukamnama date)
  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Check if a line is a short header/title line (only for first 3 lines)
  const isShortHeaderLine = (text, index) => {
    // Only check first 3 lines
    if (index >= 3) return false;
    if (!text) return false;
    const trimmed = text.trim();
    
    // Check for Mool Mantra - always treat as header
    if (trimmed.startsWith('ੴ') && trimmed.includes('ਸਤਿ ਨਾਮੁ')) {
      return true;
    }
    
    // Short lines (less than 30 chars)
    return trimmed.length < 30;
  };

  // Separate header lines from content lines (only first 3 can be headers)
  const processedLines = lines.map((line, index) => ({
    ...line,
    isHeader: isShortHeaderLine(line.gurmukhi, index),
    index,
  }));

  // Build formatted content with centered headers
  const buildGurmukhiContent = () => {
    const elements = [];
    let contentBuffer = [];

    processedLines.forEach((line, idx) => {
      if (line.isHeader) {
        // Flush content buffer first
        if (contentBuffer.length > 0) {
          elements.push({ type: 'content', text: contentBuffer.join(' ॥ ') });
          contentBuffer = [];
        }
        // Add header
        elements.push({ type: 'header', text: line.gurmukhi });
      } else {
        contentBuffer.push(line.gurmukhi);
      }
    });

    // Flush remaining content
    if (contentBuffer.length > 0) {
      elements.push({ type: 'content', text: contentBuffer.join(' ॥ ') });
    }

    return elements;
  };

  const buildPunjabiContent = () => {
    const elements = [];
    let contentBuffer = [];

    processedLines.forEach((line) => {
      if (line.isHeader) {
        if (contentBuffer.length > 0) {
          elements.push({ type: 'content', text: contentBuffer.join(' ') });
          contentBuffer = [];
        }
        // For Punjabi, show translation of header if available, otherwise skip
        if (line.translationPunjabi) {
          elements.push({ type: 'header', text: line.translationPunjabi });
        }
      } else if (line.translationPunjabi) {
        contentBuffer.push(line.translationPunjabi);
      }
    });

    if (contentBuffer.length > 0) {
      elements.push({ type: 'content', text: contentBuffer.join(' ') });
    }

    return elements;
  };

  const gurmukhiElements = buildGurmukhiContent();
  const punjabiElements = buildPunjabiContent();
  const englishText = lines.map(l => l.translationEnglish).filter(Boolean).join(" ");

  return (
    <div style={styles.container} ref={containerRef}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.khandaSmall}>☬</span>
          <span style={styles.headerTitle}>ਹੁਕਮਨਾਮਾ ਸਾਹਿਬ</span>
          <span style={styles.dateSeparator}>|</span>
          <span style={styles.dateText}>{formattedDate}</span>
        </div>
        <div style={styles.headerCenter}>
          <span style={styles.gurdwaraName}>Sikh Association Of Brantford</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.punjabiDateText}>{today.toLocaleDateString("pa-IN", { weekday: "long", day: "numeric", month: "long" })}</span>
          <span style={styles.dateSeparator}>|</span>
          <span style={styles.headerTitle}>ਅੰਗ {hukamnama.shabadData?.ang || lines[0]?.ang}</span>
        </div>
      </div>

      {/* Main Content - Split Layout */}
      <div style={styles.mainContent}>
        {/* Left Side - Gurmukhi */}
        <div style={styles.leftColumn}>
          <div style={styles.columnContent}>
            <div style={{ width: '100%' }}>
              {gurmukhiElements.map((elem, idx) => (
                elem.type === 'header' ? (
                  <div
                    key={idx}
                    style={{
                      ...styles.gurmukhiHeader,
                      fontSize: fontSizes.gurmukhi - 1,
                    }}
                  >
                    {elem.text}
                  </div>
                ) : (
                  <div
                    key={idx}
                    style={{
                      ...styles.gurmukhiText,
                      fontSize: fontSizes.gurmukhi,
                      lineHeight: fontSizes.gurmukhi > 35 ? 1.6 : fontSizes.gurmukhi > 25 ? 1.7 : 1.8,
                    }}
                  >
                    {elem.text}
                  </div>
                )
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Right Side - Punjabi */}
        <div style={styles.rightColumn}>
          <div style={styles.columnContent}>
            <div style={{ width: '100%' }}>
              {punjabiElements.length > 0 ? (
                punjabiElements.map((elem, idx) => (
                  elem.type === 'header' ? (
                    <div
                      key={idx}
                      style={{
                        ...styles.punjabiHeader,
                        fontSize: fontSizes.punjabi - 1,
                      }}
                    >
                      {elem.text}
                    </div>
                  ) : (
                    <div
                      key={idx}
                      style={{
                        ...styles.punjabiText,
                        fontSize: fontSizes.punjabi,
                        lineHeight: fontSizes.punjabi > 30 ? 1.5 : fontSizes.punjabi > 22 ? 1.6 : 1.7,
                      }}
                    >
                      {elem.text}
                    </div>
                  )
                ))
              ) : (
                <div style={styles.punjabiText}>ਅਰਥ ਉਪਲਬਧ ਨਹੀਂ</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom - English Translation */}
      <div style={styles.bottomSection}>
        <div
          style={{
            ...styles.englishText,
            fontSize: fontSizes.english,
            lineHeight: fontSizes.english < 16 ? 1.5 : 1.6,
          }}
        >
          {englishText || "Translation not available"}
        </div>
      </div>

      {/* Connection indicator */}
      <div
        style={{
          ...styles.connectionIndicator,
          backgroundColor: connected ? "#10b981" : "#ef4444",
        }}
        title={connected ? "Connected" : "Disconnected"}
      />

      {/* Google Font */}
      <link
        href="https://fonts.googleapis.com/css2?family=Noto+Sans+Gurmukhi:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
    </div>
  );
};

const styles = {
  container: {
    width: "100vw",
    height: "100vh",
    aspectRatio: "16/9",
    background: "linear-gradient(135deg, #f5ebe0 0%, #f0e6d8 50%, #ebe0d0 100%)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    fontFamily: "'Noto Sans Gurmukhi', 'Mukta Mahee', sans-serif",
  },
  loadingContainer: {
    width: "100vw",
    height: "100vh",
    background: "linear-gradient(135deg, #f5ebe0 0%, #f0e6d8 50%, #ebe0d0 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  errorContainer: {
    width: "100vw",
    height: "100vh",
    background: "linear-gradient(135deg, #f5ebe0 0%, #f0e6d8 50%, #ebe0d0 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  khanda: {
    fontSize: 80,
    color: "#b8860b",
    marginBottom: 20,
  },
  loadingText: {
    fontSize: 24,
    color: "#5c4033",
    fontFamily: "'Noto Sans Gurmukhi', sans-serif",
  },
  errorGurmukhi: {
    fontFamily: "'Noto Sans Gurmukhi', sans-serif",
    fontSize: 36,
    color: "#8b4513",
    textAlign: "center",
    marginBottom: 15,
  },
  errorEnglish: {
    fontSize: 20,
    color: "#6b5344",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "10px 30px",
    borderBottom: "2px solid rgba(139, 69, 19, 0.3)",
    background: "rgba(255, 255, 255, 0.5)",
    flexShrink: 0,
    position: "relative",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    zIndex: 1,
  },
  khandaSmall: {
    fontSize: 24,
    color: "#b8860b",
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: "#8b4513",
  },
  dateSeparator: {
    color: "rgba(139, 69, 19, 0.4)",
    fontSize: 18,
    marginLeft: 5,
  },
  dateText: {
    fontSize: 14,
    color: "#6b5344",
  },
  headerCenter: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    textAlign: "center",
  },
  gurdwaraName: {
    fontSize: 20,
    color: "#5c4033",
    fontWeight: 700,
    letterSpacing: 1,
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  headerRight: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 10,
    zIndex: 1,
  },
  punjabiDateText: {
    fontSize: 14,
    color: "#6b5344",
    fontFamily: "'Noto Sans Gurmukhi', sans-serif",
  },
  mainContent: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
    minHeight: 0,
  },
  leftColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    borderRight: "none",
    overflow: "hidden",
  },
  rightColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  divider: {
    width: 2,
    background: "linear-gradient(to bottom, transparent, rgba(139, 69, 19, 0.3), transparent)",
    flexShrink: 0,
  },
  columnHeader: {
    padding: "6px 20px",
    borderBottom: "1px solid rgba(139, 69, 19, 0.2)",
    background: "rgba(255, 255, 255, 0.3)",
    textAlign: "center",
    flexShrink: 0,
  },
  columnTitle: {
    fontSize: 14,
    color: "#8b4513",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 2,
  },
  columnContent: {
    flex: 1,
    overflow: "hidden",
    padding: "15px 25px",
    display: "flex",
    alignItems: "flex-start",
  },
  gurmukhiText: {
    color: "#1a1a1a",
    textAlign: "justify",
    wordBreak: "break-word",
  },
  gurmukhiHeader: {
    color: "#8b4513",
    textAlign: "center",
    marginBottom: 12,
    marginTop: 8,
  },
  punjabiText: {
    color: "#333333",
    textAlign: "justify",
    wordBreak: "break-word",
  },
  punjabiHeader: {
    color: "#6b5344",
    textAlign: "center",
    marginBottom: 10,
    marginTop: 6,
  },
  bottomSection: {
    borderTop: "2px solid rgba(139, 69, 19, 0.3)",
    background: "rgba(255, 255, 255, 0.6)",
    padding: "12px 30px",
    flexShrink: 0,
    minHeight: "22%",
    maxHeight: "32%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  englishHeader: {
    fontSize: 12,
    color: "#8b4513",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 2,
    marginBottom: 6,
    fontFamily: "system-ui, -apple-system, sans-serif",
    flexShrink: 0,
  },
  englishText: {
    color: "#1a3a5c",
    textAlign: "justify",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontWeight: 600,
    lineHeight: 1.5,
    flex: 1,
  },
  connectionIndicator: {
    position: "absolute",
    bottom: 10,
    left: 10,
    width: 10,
    height: 10,
    borderRadius: "50%",
  },
};

export default HukamnamaDisplay;
